import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  FaTimes,
  FaBarcode,
  FaTag,
  FaIndustry,
  FaFingerprint,
  FaRegImage,
  FaChevronLeft,
  FaChevronRight,
  FaRulerHorizontal,
  FaRulerVertical,
  FaCircleNotch,
  FaWeightHanging,
  FaFlask,
  FaLayerGroup,
  FaBoxOpen,
  FaBolt,
  FaClipboardList,
  FaInfoCircle,
  FaSpinner,
} from 'react-icons/fa'
import {
  getRepuestosPorCodigoSap,
  getImagesByMaterial,
  getCategorias,
  getSubCategorias,
  getSegmentos,
  getSubSegmentos,
  getMarcas,
} from 'src/services/axiomaService'

const primerValor = (material, claves) => {
  const clave = claves.find((k) => {
    const valor = material?.[k]
    return valor !== null && valor !== undefined && String(valor).trim() !== ''
  })
  return clave ? material[clave] : null
}

const formatearValorCampo = (valor, sufijo) => {
  if (valor === null || valor === undefined || String(valor).trim() === '') {
    return null
  }
  const texto = String(valor).trim()
  // Evitar mostrar IDs numéricos crudos como si fueran nombres.
  if (!sufijo && /^\d+$/.test(texto) && texto.length >= 5) return null
  if (!sufijo) return texto
  if (texto.toLowerCase().endsWith(sufijo.toLowerCase())) return texto
  return `${texto} ${sufijo}`
}

const esNombreVacio = (valor) => {
  if (valor === null || valor === undefined) return true
  const texto = String(valor).trim()
  if (!texto) return true
  const upper = texto.toUpperCase()
  return upper === 'SIN DEFINIR' || upper === 'N/A' || upper === 'NA'
}

const nombrePorId = (lista, id) => {
  if (id === null || id === undefined || id === '') return null
  const item = (lista || []).find(
    (x) => String(x.id) === String(id) || String(x.codigo) === String(id),
  )
  return item?.nombre || null
}

const obtenerNombre = (material) =>
  primerValor(material, [
    'revision_descripcion_correcta',
    'revision_descripcion_breve',
    'repuesto_descripcion',
    'texto_breve',
  ]) || 'Material sin nombre'

const obtenerSap = (material, codigoFallback) =>
  primerValor(material, [
    'repuesto_codigo_material_sap',
    'material',
    'codigo_sap',
    'codigo_material_sap',
  ]) ||
  codigoFallback ||
  '—'

const extraerRepuestos = (respuesta) => {
  if (!respuesta) return []
  if (Array.isArray(respuesta.repuestos)) return respuesta.repuestos
  if (Array.isArray(respuesta?.success?.repuestos)) {
    return respuesta.success.repuestos
  }
  if (Array.isArray(respuesta.success)) return respuesta.success
  if (Array.isArray(respuesta)) return respuesta
  return []
}

const medidasAEntradas = (medidas) => {
  if (!medidas || typeof medidas !== 'object') return []
  return Object.entries(medidas).sort((a, b) =>
    a[0].localeCompare(b[0], undefined, { numeric: true }),
  )
}

const formatearMedida = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '—'
  const num = Number(valor)
  return Number.isNaN(num) ? String(valor) : num.toLocaleString('es-CO')
}

// Cache de catálogos entre aperturas del modal (evita refetch + loaders).
let catalogosCache = null
let catalogosPromise = null

const cargarCatalogos = async () => {
  if (catalogosCache) return catalogosCache
  if (catalogosPromise) return catalogosPromise
  catalogosPromise = Promise.all([
    getCategorias(),
    getSubCategorias(),
    getSegmentos(),
    getSubSegmentos(),
    getMarcas(),
  ])
    .then(([categorias, subCategorias, segmentos, subSegmentos, marcas]) => {
      catalogosCache = {
        categorias: categorias?.categorias || [],
        subCategorias: subCategorias?.subcategorias || [],
        segmentos: segmentos?.segmentos || [],
        subSegmentos: subSegmentos?.subsegmentos || [],
        marcas: marcas?.marcas || [],
      }
      return catalogosCache
    })
    .catch((err) => {
      catalogosPromise = null
      throw err
    })
  return catalogosPromise
}

/**
 * Resuelve un campo: primero nombres del material, luego lookup por ID
 * en catálogos (igual que el formulario de Auditor/Axiomer).
 */
const resolverCampo = (material, campo, catalogos) => {
  const directo = primerValor(material, campo.claves || [])
  if (!esNombreVacio(directo) && !/^\d+$/.test(String(directo).trim())) {
    return formatearValorCampo(directo, campo.sufijo)
  }

  if (campo.catalogo && catalogos) {
    const id = primerValor(material, campo.ids || [])
    const nombre = nombrePorId(catalogos[campo.catalogo], id)
    if (!esNombreVacio(nombre)) {
      return formatearValorCampo(nombre, campo.sufijo)
    }
  }

  if (!esNombreVacio(directo)) {
    return formatearValorCampo(directo, campo.sufijo)
  }
  return null
}

const CAMPOS_MATERIAL = [
  {
    id: 'estado',
    etiqueta: 'Estado',
    icono: FaInfoCircle,
    claves: ['estado', 'revision_estado_ax'],
  },
  {
    id: 'marca',
    etiqueta: 'Marca',
    icono: FaIndustry,
    claves: ['revision_marca_nombre', 'marca_nombre'],
    ids: [
      'revision_marca_sugerida_id',
      'repuesto_marca_id',
      'marca_sugerida_id',
    ],
    catalogo: 'marcas',
  },
  {
    id: 'npf',
    etiqueta: 'NPF',
    icono: FaFingerprint,
    claves: ['repuesto_npf', 'npf'],
  },
  {
    id: 'categoria',
    etiqueta: 'Categoría',
    icono: FaLayerGroup,
    claves: [
      'revision_categoria_nombre',
      'categoria_nombre',
      'repuesto_categoria_nombre',
    ],
    ids: [
      'revision_categoria_sugerida_id',
      'repuesto_categoria_id',
      'categoria_id',
    ],
    catalogo: 'categorias',
  },
  {
    id: 'subcategoria',
    etiqueta: 'Subcategoría',
    icono: FaLayerGroup,
    claves: [
      'revision_subcategoria_nombre',
      'subcategoria_nombre',
      'repuesto_subcategoria_nombre',
    ],
    ids: [
      'revision_subcategoria_sugerida_id',
      'repuesto_subcategoria_id',
      'subcategoria_id',
    ],
    catalogo: 'subCategorias',
  },
  {
    id: 'segmento',
    etiqueta: 'Segmento',
    icono: FaLayerGroup,
    claves: ['revision_segmento_nombre', 'segmento_nombre'],
    ids: [
      'revision_segmento_sugerido_id',
      'repuesto_segmento_id',
      'segmento_id',
    ],
    catalogo: 'segmentos',
  },
  {
    id: 'subsegmento',
    etiqueta: 'Subsegmento',
    icono: FaLayerGroup,
    claves: ['revision_subsegmento_nombre', 'subsegmento_nombre'],
    ids: [
      'revision_subsegmento_sugerido_id',
      'repuesto_subsegmento_id',
      'subsegmento_id',
    ],
    catalogo: 'subSegmentos',
  },
  {
    id: 'largo',
    etiqueta: 'Largo',
    icono: FaRulerHorizontal,
    claves: ['revision_largo_mm', 'repuesto_largo_mm', 'largo'],
    sufijo: 'mm',
  },
  {
    id: 'ancho',
    etiqueta: 'Ancho',
    icono: FaRulerHorizontal,
    claves: ['revision_ancho_mm', 'repuesto_ancho_mm', 'ancho'],
    sufijo: 'mm',
  },
  {
    id: 'alto',
    etiqueta: 'Alto',
    icono: FaRulerVertical,
    claves: ['revision_alto_mm', 'repuesto_alto_mm', 'alto'],
    sufijo: 'mm',
  },
  {
    id: 'diametro',
    etiqueta: 'Diámetro',
    icono: FaCircleNotch,
    claves: ['revision_diametro_mm', 'repuesto_diametro_mm'],
    sufijo: 'mm',
  },
  {
    id: 'peso',
    etiqueta: 'Peso',
    icono: FaWeightHanging,
    claves: ['revision_peso_g', 'repuesto_peso_g'],
    sufijo: 'g',
  },
  {
    id: 'composicion',
    etiqueta: 'Composición',
    icono: FaFlask,
    claves: ['revision_composicion', 'repuesto_composicion'],
  },
  {
    id: 'uso',
    etiqueta: 'Uso / aplicación',
    icono: FaBoxOpen,
    claves: ['revision_uso_aplicacion', 'repuesto_uso_aplicacion'],
  },
  {
    id: 'especificaciones_electricas',
    etiqueta: 'Especificaciones eléctricas',
    icono: FaBolt,
    claves: ['revision_especificaciones_electricas'],
  },
  {
    id: 'observacion',
    etiqueta: 'Observación',
    icono: FaClipboardList,
    claves: ['revision_observacion'],
  },
]

const GaleriaImagenes = ({ imagenes, cargando }) => {
  const [activa, setActiva] = useState(0)

  useEffect(() => {
    setActiva(0)
  }, [imagenes])

  if (cargando) {
    return (
      <div className="especificaciones-modal-media">
        <div className="especificaciones-modal-media-principal especificaciones-modal-skeleton" />
        <div className="especificaciones-modal-thumbs">
          <div className="especificaciones-modal-thumb especificaciones-modal-skeleton" />
          <div className="especificaciones-modal-thumb especificaciones-modal-skeleton" />
          <div className="especificaciones-modal-thumb especificaciones-modal-skeleton" />
        </div>
      </div>
    )
  }

  if (!imagenes || imagenes.length === 0) {
    return (
      <div className="especificaciones-modal-media">
        <div className="especificaciones-modal-media-principal especificaciones-modal-media-vacia">
          <FaRegImage />
          <span>Sin imágenes</span>
        </div>
      </div>
    )
  }

  const indice = Math.min(activa, imagenes.length - 1)
  const total = imagenes.length

  return (
    <div className="especificaciones-modal-media">
      <div className="especificaciones-modal-media-principal">
        <img
          src={imagenes[indice].signed_url}
          alt={imagenes[indice].nombre_objeto || 'Imagen del material'}
        />
        {total > 1 && (
          <>
            <button
              type="button"
              className="especificaciones-modal-media-nav especificaciones-modal-media-nav-prev"
              onClick={() => setActiva((prev) => (prev - 1 + total) % total)}
              aria-label="Imagen anterior"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className="especificaciones-modal-media-nav especificaciones-modal-media-nav-next"
              onClick={() => setActiva((prev) => (prev + 1) % total)}
              aria-label="Imagen siguiente"
            >
              <FaChevronRight />
            </button>
            <span className="especificaciones-modal-media-contador">
              {indice + 1} / {total}
            </span>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="especificaciones-modal-thumbs">
          {imagenes.map((img, idx) => (
            <button
              type="button"
              key={img.nombre_objeto || img.signed_url || idx}
              className={`especificaciones-modal-thumb ${
                idx === indice ? 'active' : ''
              }`}
              onClick={() => setActiva(idx)}
            >
              <img
                src={img.signed_url}
                alt={img.nombre_objeto || `Miniatura ${idx + 1}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Modal de solo lectura con imágenes y datos del material.
 * Se abre al hacer clic en un código SAP de la tabla de especificaciones.
 */
const MaterialDetalleModal = ({ open, codigo, fila, onClose }) => {
  const [material, setMaterial] = useState(null)
  const [imagenes, setImagenes] = useState([])
  const [catalogos, setCatalogos] = useState(catalogosCache)
  const [cargando, setCargando] = useState(false)
  const [cargandoImagenes, setCargandoImagenes] = useState(false)
  const [error, setError] = useState(null)
  const seqRef = useRef(0)

  const cargar = useCallback(async (codigoSap) => {
    if (!codigoSap) return
    const seq = ++seqRef.current
    setCargando(true)
    setCargandoImagenes(true)
    setError(null)
    setMaterial(null)
    setImagenes([])

    try {
      const [respMaterial, cats] = await Promise.all([
        getRepuestosPorCodigoSap(String(codigoSap)),
        cargarCatalogos().catch((err) => {
          console.error('Error al cargar catálogos:', err)
          return catalogosCache
        }),
      ])
      if (seq !== seqRef.current) return
      if (cats) setCatalogos(cats)

      const repuestos = extraerRepuestos(respMaterial)
      const mat = repuestos[0]
      if (!mat) {
        setError(`No se encontró el material ${codigoSap}`)
        setCargando(false)
        setCargandoImagenes(false)
        return
      }
      setMaterial(mat)
      setCargando(false)

      try {
        const sap = obtenerSap(mat, codigoSap)
        const respImg = await getImagesByMaterial(sap)
        if (seq !== seqRef.current) return
        const imgs = (respImg?.success || []).filter((i) => i?.signed_url)
        const favorita = mat?.revision_imagen_favorita
        const ordenadas = favorita
          ? [
              ...imgs.filter((i) => i.nombre_objeto === favorita),
              ...imgs.filter((i) => i.nombre_objeto !== favorita),
            ]
          : imgs
        setImagenes(ordenadas)
      } finally {
        if (seq === seqRef.current) setCargandoImagenes(false)
      }
    } catch (err) {
      if (seq !== seqRef.current) return
      console.error('Error al cargar detalle del material:', err)
      setError(`Error al cargar el material ${codigoSap}`)
      setCargando(false)
      setCargandoImagenes(false)
    }
  }, [])

  useEffect(() => {
    if (!open || !codigo) return undefined
    cargar(codigo)
    return () => {
      seqRef.current += 1
    }
  }, [open, codigo, cargar])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  const sap = obtenerSap(material, codigo)
  const nombre = material ? obtenerNombre(material) : null
  const entradasMedidas = medidasAEntradas(fila?.medidas)
  const plantillas = Array.isArray(fila?.plantillas) ? fila.plantillas : []
  const especificacionesProducto =
    fila?.especificaciones_producto ||
    material?.revision_especificaciones_producto ||
    null
  const descripcionBreve = primerValor(material, [
    'revision_descripcion_breve',
    'repuesto_descripcion',
  ])

  return (
    <div
      className="especificaciones-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="especificaciones-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="especificaciones-modal-title"
      >
        <div className="especificaciones-modal-header">
          <div className="especificaciones-modal-header-text">
            <h2 id="especificaciones-modal-title">
              <FaBarcode />
              Material {sap}
            </h2>
            {nombre && (
              <p className="especificaciones-modal-subtitle">{nombre}</p>
            )}
          </div>
          <button
            type="button"
            className="especificaciones-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>

        <div className="especificaciones-modal-body">
          {cargando && (
            <div className="especificaciones-modal-loading">
              <FaSpinner className="especificaciones-spin" />
              Cargando información del material...
            </div>
          )}

          {!cargando && error && (
            <div className="especificaciones-modal-error">{error}</div>
          )}

          {!cargando && !error && material && (
            <div className="especificaciones-modal-grid">
              <div className="especificaciones-modal-media-col">
                <GaleriaImagenes
                  imagenes={imagenes}
                  cargando={cargandoImagenes}
                />
              </div>

              <div className="especificaciones-modal-info">
                <div className="especificaciones-modal-name">
                  <FaTag />
                  <span>{nombre}</span>
                </div>

                {descripcionBreve && descripcionBreve !== nombre && (
                  <p className="especificaciones-modal-desc-breve">
                    {descripcionBreve}
                  </p>
                )}

                {(entradasMedidas.length > 0 || plantillas.length > 0) && (
                  <div className="especificaciones-modal-section">
                    <h3>Medidas generadas</h3>
                    {entradasMedidas.length > 0 ? (
                      <div className="especificaciones-medidas">
                        {entradasMedidas.map(([clave, valor]) => (
                          <span
                            key={`${clave}-${valor}`}
                            className="especificaciones-medida-chip"
                          >
                            <span className="especificaciones-medida-clave">
                              {clave}
                            </span>
                            <span className="especificaciones-medida-valor">
                              {formatearMedida(valor)}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="especificaciones-sin-medidas">
                        Sin medidas parseadas
                      </span>
                    )}
                    {plantillas.length > 0 && (
                      <div className="especificaciones-modal-plantillas">
                        <span className="especificaciones-modal-plantillas-label">
                          Plantillas:
                        </span>
                        {plantillas.map((p) => (
                          <span
                            key={p}
                            className="especificaciones-plantilla-chip"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {especificacionesProducto && (
                  <div className="especificaciones-modal-section">
                    <h3>Especificaciones del producto</h3>
                    <p className="especificaciones-modal-texto-largo">
                      {especificacionesProducto}
                    </p>
                  </div>
                )}

                <div className="especificaciones-modal-specs">
                  {CAMPOS_MATERIAL.map((campo) => {
                    const Icono = campo.icono
                    const valor = resolverCampo(material, campo, catalogos)
                    return (
                      <div
                        key={campo.id}
                        className={`especificaciones-modal-spec ${
                          campo.ancho === 'full'
                            ? 'especificaciones-modal-spec-full'
                            : ''
                        }`}
                      >
                        <div className="especificaciones-modal-spec-icon">
                          <Icono />
                        </div>
                        <div className="especificaciones-modal-spec-body">
                          <span className="especificaciones-modal-spec-label">
                            {campo.etiqueta}
                          </span>
                          <span
                            className={`especificaciones-modal-spec-value ${
                              valor ? '' : 'empty'
                            }`}
                          >
                            {valor || 'Sin información'}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MaterialDetalleModal
